/**
 * Licensed Materials - Property of IBM
 * IBM Business Analytics (C) Copyright IBM Corp. 2020, 2022
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

define([
	'./DashboardCore',
	'../core/RuntimeEnvironment',
	'../../dashboard/loader/BoardLoaderHelper',
	'./LegacyViewControllers'
], (DashboardCore, RuntimeEnvironment, BoardLoaderHelper, LegacyViewControllers) => {

	/**
	 * @class DashboardFactory
	 * @classdesc Factory class creating an instance of DashboardAPI
	*/
	class DashboardFactory {
		constructor() {
			this._bootstrapFeatures = {};
			this._featureDefinitions = {
				dashboard: [],
				content: []
			};
		}

		/**
		 * Initializes the DashboardFactory; creates default bootstrap features if none is provided
		 * @param {Object} initParams - map of name-instance of the bootstrap features
		 * @param {Object} initParams.bootstrapFeatures - map of name-instance of the bootstrap features
		 * @param {Array} initParams.dashboardFeatures - array listing all the dashboard feature definitions
		 * @param {Array} initParams.contentFeatures - array listing all the content feature definitions
		 * @return {Promise} - resolved if init is successful, rejected if an error occurred during the init process
		 * @throws throws an exception if no parameter is provided
		 */
		init({ bootstrapFeatures, dashboardFeatures, contentFeatures }) {
			if (typeof bootstrapFeatures['Logger'] === 'undefined') {
				this._bootstrapFeatures['Logger'] = console;
			} else {
				this._bootstrapFeatures['Logger'] = bootstrapFeatures['Logger'];
			}
			this._featureDefinitions.dashboard = dashboardFeatures ? dashboardFeatures : [];
			this._featureDefinitions.content = contentFeatures ? contentFeatures : [];
			return Promise.resolve();
		}

		initializeRuntime() {
			if (this.runtimeEnv){
				return Promise.resolve(this.runtimeEnv);
			} else {
				return this.getBootstrapFeatures().then((features) => {
					this.runtimeEnv = new RuntimeEnvironment(features);
					return this.runtimeEnv;
				});
			}
		}

		getBootstrapFeatures() {
			throw new Error('This method should be overriden by the child class');
		}

		/**
		 * Returns the instance of a bootstrap feature
		 * @param {String} name - name of the feature
		 * @returns {Object} instance of the feature or undefined if unknown
		 */
		getBootstrapFeature(name) {
			return this._bootstrapFeatures[name];
		}

		/**
		 * Returns the dashboard features
		 * @returns [] Json array describing the dashboard features
		 */
		getDashboardFeatures() {
			return this._featureDefinitions.dashboard;
		}

		/**
		 * Returns the content features
		 * @returns [] Json array describing the content features
		 */
		getContentFeatures() {
			return this._featureDefinitions.content;
		}

		/**
		 * creates an instance of DashboardAPI based on the passed spec
		 * @param {Object} options - map of parameters
		 * @param {Object} options.assetId - dashboard asset id
		 * @param {Object} options.spec - dashboard spec
		 * @param {Object} [options.profiles] - array of feature profiles to consider when instantiating the dashboard API, core profile is taken if undefined
		 * @returns {Promise} - resolved when the CoreDashboard instance is created
		 */
		create(options) {
			let env, boardLoaderHelper;
			return this.initializeRuntime().then((runtimeService) => {
				const runtimeOptions = {
					featuresOverride: this._featuresOverride,
					assetId: options.assetId,
					spec: options.spec,
					profiles: options.profiles
				};
				if(!runtimeOptions.assetId && !runtimeOptions.spec) {
					runtimeOptions.spec = runtimeService.getDefaultSpec();
				}
				return runtimeService.getRuntimeEnvironment(runtimeOptions);
			}).then((environment) => {
				env = environment;
				boardLoaderHelper = new BoardLoaderHelper(env.dashboardApi);
				return boardLoaderHelper.loadLayoutExtensions(env.layoutExtensions);
			}).then(layoutExtensions => {
				boardLoaderHelper.registerLayoutExtensions(layoutExtensions);
				const legacyViewControllers = new LegacyViewControllers({
					environment: env
				});
				env.dashboardApi.getFeature('FeatureRegistry.internal').registerFeature('LegacyViewControllers', legacyViewControllers, { profiles: ['core'] });
				return new DashboardCore({
					dashboardAPI: env.dashboardApi,
					internalDashboardAPI: env.internalDashboardAPI
				}).getAPI();
			});


		}
	}

	return DashboardFactory;
});
